import React, { useMemo } from 'react';
import SiteCharts from '../../../components/SiteCharts';
import { volteCellChartConfig } from '../cellChartConfigs';

/**
 * Props for the VolteEstablishmentCell component
 */
interface VolteEstablishmentCellProps {
  /** Callback function to toggle fullscreen mode */
  onFullscreenToggle: () => void;
  /** Current fullscreen state */
  isFullscreen: boolean;
  /** Cell identifier passed to API as cell_name parameter */
  cellName: string;
}

/**
 * VoLTE Establishment Success Rate Chart (LTE Cell Level)
 * 
 * Displays Voice over LTE (VoLTE) establishment metrics for individual LTE cells:
 * - VoLTE Establishment Success (count)
 * - VoLTE Establishment Attempt (count)
 * - VoLTE Establishment Success Rate (percentage)
 * 
 * This component is a thin wrapper around the SiteCharts template component,
 * configured with cell-level VoLTE metrics from cellChartConfigs.ts
 * 
 * @component
 * @example
 * ```tsx
 * <VolteEstablishmentCell
 *   onFullscreenToggle={() => setFullscreen(!fullscreen)}
 *   isFullscreen={false}
 *   cellName="173716_145"
 * />
 * ```
 * 
 * @see {@link ../cellChartConfigs.ts} - Chart configuration (volteCellChartConfig)
 * @see {@link ../../../components/SiteCharts.tsx} - Template component
 * @see API: /api/lte-cell/volte-establishment?cell_name={cellName}
 */

/**
 * VoLTE Establishment Cell-Level Chart Component
 * 
 * Displays VoLTE (Voice over LTE) establishment metrics for a specific cell:
 * - VoLTE Establishment Success (count)
 * - VoLTE Establishment Attempt (count)
 * - VoLTE Establishment Success Rate (percentage)
 * 
 * Uses the SiteCharts template for consistent visualization.
 */
const VolteEstablishmentCell: React.FC<VolteEstablishmentCellProps> = ({ 
  onFullscreenToggle, 
  isFullscreen,
  cellName
}) => {
  // Memoize additionalParams to prevent infinite re-renders
  const additionalParams = useMemo(() => ({ cell_name: cellName }), [cellName]);

  return (
    <SiteCharts 
      config={volteCellChartConfig}
      onFullscreenToggle={onFullscreenToggle}
      isFullscreen={isFullscreen}
      additionalParams={additionalParams}
    />
  );
};

export default VolteEstablishmentCell;
